/**
 * Reading position — tracks the last verse the user was reading at the
 * chapter level, persisted to localStorage so the reader opens at exactly
 * the right place next session.
 *
 * Separate from resume-store (which tracks audio playback seconds) and
 * history-store (which tracks book+chapter visits). This is the text
 * reading scroll position.
 */

const STORAGE_KEY = '_rp1';

export type ReadingPosition = {
  bookId: string;
  chapter: number;
  verseRef: string; // OSIS-style, e.g. "ROM.8.15"
  savedAt: number;
};

function load(): ReadingPosition | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReadingPosition>;
    if (
      typeof parsed.bookId === 'string' &&
      typeof parsed.chapter === 'number' &&
      typeof parsed.verseRef === 'string' &&
      typeof parsed.savedAt === 'number'
    ) {
      return parsed as ReadingPosition;
    }
    return null;
  } catch {
    return null;
  }
}

function persist(pos: ReadingPosition): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* quota — ignore */
  }
}

export function getReadingPosition(): ReadingPosition | null {
  return load();
}

export function saveReadingPosition(
  bookId: string,
  chapter: number,
  verseRef: string,
): void {
  persist({
    bookId: bookId.toUpperCase(),
    chapter,
    verseRef,
    savedAt: Date.now(),
  });
}

// One-shot pending scroll ref — set on startup, consumed by ChapterWithVerses
// once the relevant chapter mounts. Avoids reactive complexity.
let _pendingScroll: string | null = null;

export function setPendingScroll(verseRef: string): void {
  _pendingScroll = verseRef;
}

export function consumePendingScroll(): string | null {
  const v = _pendingScroll;
  _pendingScroll = null;
  return v;
}
